import React, { useEffect } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Box, FormHelperText } from '@mui/material';
import { Button } from '~/index.ts';
import { HookFormTextField } from '~/components/atoms/HookForm/TextField/index.ts';
import {
  makeSchemaOptional,
  optionalEmailSchema,
  optionalPhoneSchema,
} from '~/utils/zodSchemas.ts';
import {
  formatPhone,
  formatPhoneOnChangeEvent,
} from '~/utils/formatters/index.ts';

const schema = z
  .object({
    fullName: makeSchemaOptional(
      z.string().regex(/\S+ +\S+/, { message: 'Enter a first and last name' }),
    ),
    email: optionalEmailSchema,
    phone: optionalPhoneSchema,
    formError: z.void(),
  })
  .refine(
    ({ fullName, email, phone }) =>
      fullName !== '' || email !== '' || phone !== '',
    {
      message: 'Any of full name, email, or phone is required',
      path: ['formError'],
    },
  );

type FormSchema = z.infer<typeof schema>;

export interface CustomerSearchQuery {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

function getDefaultValues(initialValues: CustomerSearchQuery): FormSchema {
  return {
    fullName: [initialValues.firstName, initialValues.lastName]
      .filter(Boolean)
      .join(' '),
    email: initialValues.email || '',
    phone: formatPhone(initialValues.phone),
  };
}

export interface CustomerSearchRequeryFormProps {
  onSearch: (query: CustomerSearchQuery) => void;
  initialValues: CustomerSearchQuery;
}

const textFieldStyles = { flex: '1 1 auto', backgroundColor: '#FFFFFF' };

export const CustomerSearchRequeryForm: React.FC<
  CustomerSearchRequeryFormProps
> = ({ onSearch, initialValues }) => {
  const { control, handleSubmit, formState, clearErrors, reset, trigger } =
    useForm<FormSchema>({
      mode: 'onTouched',
      resolver: zodResolver(schema),
      defaultValues: getDefaultValues(initialValues),
    });

  useEffect(() => {
    reset({
      ...getDefaultValues(initialValues),
    });
  }, [initialValues, reset]);

  useEffect(() => {
    // Customer search results can be initially opened with an invalid form
    // if the user entered only a first name in the modal. In that case we
    // want to show an error on the name field.
    trigger();
  }, [initialValues, trigger]);

  const { errors } = formState;
  const formError = errors.formError;
  const clearFormError = (): void => clearErrors('formError');

  const submit = handleSubmit(({ fullName, email, phone }) => {
    const reqBody: CustomerSearchQuery = {};
    if (fullName) {
      const [firstName, ...lastNameParts] = fullName.trim().split(' ');
      reqBody.firstName = firstName;
      reqBody.lastName = lastNameParts.join(' ');
    }

    if (email) {
      reqBody.email = email;
    }

    if (phone) {
      reqBody.phone = phone;
    }

    onSearch(reqBody);
  });

  // add extra height to the parent Box to make room for text field errors
  return (
    <Box sx={{ height: '76px' }}>
      <Box
        sx={{ display: 'flex', gap: '16px' }}
        component="form"
        onSubmit={submit}
      >
        <HookFormTextField
          control={control}
          name="fullName"
          label="Full name"
          onChange={clearFormError}
          styleOverrides={textFieldStyles}
          autoComplete="off"
        />
        <HookFormTextField
          control={control}
          name="email"
          label="Email"
          onChange={clearFormError}
          styleOverrides={textFieldStyles}
          autoComplete="off"
        />
        <HookFormTextField
          control={control}
          name="phone"
          label="Phone"
          transformOnChangeValue={formatPhoneOnChangeEvent}
          onChange={clearFormError}
          styleOverrides={textFieldStyles}
          autoComplete="off"
        />
        <Button
          variant="contained"
          color="secondary"
          styleOverrides={{ height: '53px', minWidth: '122px' }}
          type="submit"
        >
          Refine search
        </Button>
      </Box>
      {formError && <FormHelperText error>{formError.message}</FormHelperText>}
    </Box>
  );
};
